import { createContext, FunctionComponent, useEffect, useState } from "react";

function themeSwitch(theme: string): void {
  switch (theme) {
    case "dark":
      document.documentElement.setAttribute("color-mode", "dark");
      break;
    case "light":
      document.documentElement.setAttribute("color-mode", "light");
      break;
    default:
      document.documentElement.setAttribute("color-mode", "dark");
      break;
  }
}

const getMediaQueryList = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");
const getBrowserTheme = () => {
  const mql = getMediaQueryList();
  return mql && mql.matches ? "dark" : "light";
};

interface IThemeContext {
  hasTheme: string;
  toggleTheme(): void;
}

export const ThemeContext = createContext<IThemeContext>({
  hasTheme: "light",
  toggleTheme: () => {},
});

export const ThemeProvider: FunctionComponent = ({ children }) => {
  const [hasTheme, setTheme] = useState("light");

  useEffect(() => {
    const local = getLocalStorageItem("appTheme", ["light", "dark"]);
    const browser = getBrowserTheme();
    if (local) {
      setTheme(local);
      themeSwitch(local);
    }
    if (browser && !local) {
      setTheme(browser);
      themeSwitch(browser);
    }
  }, []);

  const toggleTheme = (): void => {
    let newTheme;

    if (hasTheme === "light") {
      newTheme = "dark";
    } else {
      newTheme = "light";
    }
    setLocalStorageItem("appTheme", newTheme);
    setTheme(newTheme);
    themeSwitch(newTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        hasTheme,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const getLocalStorageItem = (
  itemName: string,
  testItems: string[]
): string | undefined => {
  try {
    const localTheme = localStorage && localStorage.getItem(itemName);
    if (localTheme && testItems.includes(localTheme)) {
      return localTheme;
    }
  } catch (err: any) {
    console.warn("Can’t access local storage item:", err.message);
  }
};

export const setLocalStorageItem = (
  itemName: string,
  itemValue: string
): void => {
  try {
    localStorage && localStorage.setItem(itemName, itemValue);
  } catch (err: any) {
    console.warn("Can’t write to local storage:", err.message);
  }
};
